/**
 * Name → glyph registry (UID-136, ADR-0054).
 *
 * This is the expand-contract migration off the single `@ant-design/icons`
 * re-export barrel (`components/icons.tsx`, the only file in the app that
 * imports `@ant-design/icons` directly). Every entry below now resolves to
 * a LOCAL, vendored glyph (`lucide-glyphs.tsx`) whose path data is copied
 * from the design source (`Fictiv Platform.dc.html`), matching the
 * prototype's stroke-icon language — NOT the `@ant-design/icons` glyph the
 * call site used to render. That is the whole point of this ticket: the
 * running app's icons were visibly a different design language (stroke
 * width / fill / corner radius, and in a few cases an entirely different
 * pictogram) from the prototype.
 *
 * UID-301: `filter` (the TopBar search-field control) is now migrated too —
 * the design draws a lucide "sliders" glyph (`Fictiv Platform.dc.html:316`),
 * not Ant's `FilterOutlined` funnel it previously fell back to. With that, the
 * shell no longer imports any `@ant-design/icons` glyph through this registry.
 *
 * Scope: only the names the global shell (`Sidebar.tsx`, `TopBar.tsx`) and
 * the RFQ vertical (`RfqCard`, `RfqEtaChip`,
 * `StatusStepper`) actually render today. Every other vertical's icons stay
 * on the direct `components/icons` import until their own follow-up ticket.
 */
import type { ComponentType } from 'react'
import {
  AlertTriangleGlyph,
  BoxGlyph,
  CheckCircleGlyph,
  CheckGlyph,
  ChevronDownGlyph,
  ClockGlyph,
  CopyGlyph,
  DownloadGlyph,
  FileTextGlyph,
  HouseGlyph,
  LayoutGridGlyph,
  LockGlyph,
  LogOutGlyph,
  MenuGlyph,
  PackageCheckGlyph,
  PanelLeftGlyph,
  PlusGlyph,
  SearchGlyph,
  SettingsGlyph,
  ShareGlyph,
  ShoppingCartGlyph,
  SlidersGlyph,
  SquarePenGlyph,
  UploadGlyph,
} from './lucide-glyphs'

/**
 * Closed union of every icon `<Icon>` can render — a typo'd name is a
 * compile error, not a silent missing-icon render. Stable, semantic,
 * kebab-case names (never the Ant class name), so the migrate phase can
 * swap the underlying glyph without renaming anything at the call site.
 */
export type IconName =
  // Global shell (Sidebar.tsx / TopBar.tsx)
  | 'home'
  // The Sidebar "Search catalog" nav item — the design renders this as a
  // grid/browse pictogram, NOT the magnifying-glass 'search' icon below
  // (the app previously reused one Ant glyph for both, which is itself
  // one of the icon-language mismatches this ticket fixes).
  | 'catalog'
  // TopBar's actual search-input leading icon (a real magnifying glass).
  | 'search'
  | 'cart'
  | 'shopping-bag'
  // The Sidebar "Part library" nav item — distinct pictogram from 'document'
  // below (the app previously reused one glyph for both nav items too).
  | 'library'
  | 'document'
  | 'plus'
  // The design source rotates this SAME sidebar-panel glyph 180deg for the
  // expand state rather than using a second icon — one name, one glyph;
  // the call site (`Sidebar.tsx`) applies the rotation via CSS.
  | 'sidebar-collapse'
  // TopBar mobile hamburger that opens the off-canvas sidebar drawer (≤900px).
  | 'menu'
  | 'filter'
  | 'chevron-down'
  // RFQ vertical (RfqCard.tsx / RfqEtaChip.tsx / StatusStepper.tsx)
  // NOTE (UID-438/ADR-0143 D1): `exclamation-circle` below is left in the
  // registry but is now UNREFERENCED — `ShareHandoffModal`, its only consumer,
  // was retired with this ticket. Deliberately not removed: pruning a member of
  // a shared registry is a wider change than this ticket, and an unused lookup
  // entry is inert. Flagged here so it is a known orphan, not a silent one.
  | 'download'
  | 'lock'
  | 'share'
  | 'clock'
  | 'check'
  | 'check-circle-filled'
  | 'exclamation-circle'
  // TopBar "Upload" action (UID-244, 2026-08-23 design refresh).
  | 'upload'
  // Sidebar help card (UID-301) — copy-to-clipboard next to the support emails.
  | 'copy'
  // Account menu "Log out" (UID-301).
  | 'log-out'
  // The RFQ detail rail's "Edit RFQ" control (UID-265b, ADR-0093 D6) — the
  // design's own square-pen glyph (`Fictiv Platform.dc.html:3725`).
  | 'edit'
  // Account menu "Account settings" (UID-343).
  | 'settings'

/**
 * The minimal prop surface `Icon.tsx` actually passes to a glyph component.
 * Every `@ant-design/icons` component accepts `React.HTMLProps<HTMLSpanElement>`
 * (see `AntdIconProps`/`IconBaseProps`), which is a superset of this — so a
 * real Ant component is always structurally assignable here. Kept narrow and
 * local (rather than importing an `@ant-design/icons` internal type) so this
 * registry's public shape doesn't depend on that package's un-exported types.
 */
export interface IconGlyphProps {
  readonly className?: string | undefined
  readonly style?: React.CSSProperties | undefined
  readonly title?: string | undefined
  readonly role?: string | undefined
  readonly 'aria-hidden'?: 'true' | 'false' | undefined
  readonly 'aria-label'?: string | undefined
}

export const ICON_REGISTRY: Record<IconName, ComponentType<IconGlyphProps>> = {
  home: HouseGlyph,
  catalog: LayoutGridGlyph,
  search: SearchGlyph,
  cart: ShoppingCartGlyph,
  'shopping-bag': PackageCheckGlyph,
  library: BoxGlyph,
  document: FileTextGlyph,
  plus: PlusGlyph,
  'sidebar-collapse': PanelLeftGlyph,
  menu: MenuGlyph,
  filter: SlidersGlyph,
  'chevron-down': ChevronDownGlyph,
  download: DownloadGlyph,
  lock: LockGlyph,
  share: ShareGlyph,
  clock: ClockGlyph,
  check: CheckGlyph,
  'check-circle-filled': CheckCircleGlyph,
  'exclamation-circle': AlertTriangleGlyph,
  upload: UploadGlyph,
  copy: CopyGlyph,
  'log-out': LogOutGlyph,
  edit: SquarePenGlyph,
  settings: SettingsGlyph,
}
